import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  SEEN_KEY,
  digestOf,
  markNoticesSeen,
  noticeKey,
  noticesFor,
  unseenOf,
} from './lib/notifications';
import { readSnapshot, writeSnapshot } from './lib/statusWatch';

/**
 * What this person needs to know, on the screen they already have open
 * (subsystem 5).
 *
 * Three rules keep it from becoming wallpaper. Only an `action` notice is
 * allowed to be prominent. Dismissing is per notice and remembered, so a
 * thing you have dealt with does not come back. And a notice always links
 * somewhere — a notification you cannot act on is an interruption.
 *
 * Seen state lives in the browser, like the console's status watch: it will
 * not follow someone to another machine. The server-side flag is the right
 * long-term home, and is in the backend requirements doc.
 */

export const NOTICE_CSS = `
.nt-wrap { display: grid; gap: 8px; margin-bottom: 14px; }
.nt-digest { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; font-size: 12.5px; color: var(--ink-3); }
.nt-digest__text { font-weight: 650; }
.nt-item { display: flex; align-items: flex-start; gap: 10px; border-radius: var(--radius-sm); padding: 10px 12px; border: 1px solid var(--line); background: var(--surface); }
.nt-item--action { border-color: #FECDCA; background: #FEF3F2; }
.nt-item--warn { border-color: #FDE68A; background: #FFFBEB; }
.nt-item__dot { flex: 0 0 auto; width: 7px; height: 7px; border-radius: 50%; margin-top: 6px; background: var(--ink-4); }
.nt-item--action .nt-item__dot { background: #B42318; }
.nt-item--warn .nt-item__dot { background: #B45309; }
.nt-item__main { flex: 1 1 auto; min-width: 0; }
.nt-item__title { font-size: 13px; font-weight: 650; color: var(--ink); margin: 0; overflow-wrap: anywhere; }
.nt-item--action .nt-item__title { color: #912018; }
.nt-item--warn .nt-item__title { color: #92400E; }
.nt-item__detail { font-size: 12px; color: var(--ink-4); margin: 2px 0 0; }
.nt-item__go { font-size: 12.5px; font-weight: 700; color: var(--ink); text-decoration: none; border-bottom: 1px solid var(--line); }
.nt-item__go:hover { border-bottom-color: currentColor; }
.nt-item__close { all: unset; cursor: pointer; flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; min-width: 44px; min-height: 44px; margin: -10px -8px -10px 0; font-size: 17px; line-height: 1; color: var(--ink-4); border-radius: 6px; }
.nt-item__close:hover { color: var(--ink); }
.nt-item__close:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
.nt-more { all: unset; cursor: pointer; font-size: 12.5px; font-weight: 650; color: var(--ink-3); text-decoration: underline; }
.nt-more:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
`;

/** How many to show before the rest fold away. */
const VISIBLE = 3;

export default function NoticeStrip({ audience, context, storage, max = VISIBLE }) {
  const [seen, setSeen] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const store = storage || (typeof window === 'undefined' ? null : window.localStorage);

  useEffect(() => {
    const saved = readSnapshot(store, SEEN_KEY);
    setSeen(Array.isArray(saved) ? saved : []);
  }, [store]);

  const all = useMemo(() => noticesFor(audience, context), [audience, context]);
  const live = useMemo(() => unseenOf(all, seen), [all, seen]);

  function dismiss(notice) {
    const next = markNoticesSeen(seen, [notice]);
    setSeen(next);
    writeSnapshot(store, SEEN_KEY, next);
  }

  if (live.length === 0) return null;

  const shown = expanded ? live : live.slice(0, max);
  const hidden = live.length - shown.length;

  return (
    <div className="nt-wrap" aria-label="Updates" role="region">
      <div className="nt-digest">
        <span className="nt-digest__text">{digestOf(live)}</span>
      </div>

      {shown.map((notice) => (
        <div key={noticeKey(notice)} className={`nt-item nt-item--${notice.severity}`}>
          <span className="nt-item__dot" aria-hidden="true" />
          <div className="nt-item__main">
            <p className="nt-item__title">{notice.title}</p>
            {notice.detail ? <p className="nt-item__detail">{notice.detail}</p> : null}
          </div>
          {notice.href ? (
            <Link className="nt-item__go" to={notice.href}>
              Open
            </Link>
          ) : null}
          <button
            type="button"
            className="nt-item__close"
            onClick={() => dismiss(notice)}
            aria-label={`Dismiss: ${notice.title}`}
          >
            ×
          </button>
        </div>
      ))}

      {hidden > 0 ? (
        <button type="button" className="nt-more" onClick={() => setExpanded(true)}>
          Show {hidden} more
        </button>
      ) : null}
    </div>
  );
}
